class HomepageInfluencerVideos extends HTMLElement {
  constructor() {
    super();
    this.cards = [];
    this.videos = [];
    this.track = null;
    this.prevButton = null;
    this.nextButton = null;
    this.activeCard = null;
    this.observer = null;
    this.scrollObserver = null;
    this.desktopQuery = window.matchMedia('(min-width: 768px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.handleCardMouseEnter = this.handleCardMouseEnter.bind(this);
    this.handleCardMouseLeave = this.handleCardMouseLeave.bind(this);
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
    this.handleTrackScroll = this.handleTrackScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleMuteClick = this.handleMuteClick.bind(this);
    this.handleVideoPlaying = this.handleVideoPlaying.bind(this);
    this.handleVideoWaiting = this.handleVideoWaiting.bind(this);
    this.handleVideoError = this.handleVideoError.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('[data-influencer-track]');
    this.prevButton = this.querySelector('[data-influencer-prev]');
    this.nextButton = this.querySelector('[data-influencer-next]');
    this.cards = Array.from(this.querySelectorAll('[data-influencer-card]'));
    this.videos = this.cards.map((card) => card.querySelector('video')).filter(Boolean);
    this.autoplayFirst = this.dataset.autoplayFirst === 'true';
    this.autoplaySound = this.dataset.autoplaySound === 'true';

    this.cards.forEach((card) => {
      card.addEventListener('mouseenter', this.handleCardMouseEnter);
      card.addEventListener('mouseleave', this.handleCardMouseLeave);
      const muteBtn = card.querySelector('[data-influencer-toggle-mute]');
      if (muteBtn) {
        muteBtn.addEventListener('click', this.handleMuteClick);
        // Default: muted
        card.classList.add('is-muted');
      }
      const video = card.querySelector('video');
      if (video) {
        video.addEventListener('playing', this.handleVideoPlaying);
        video.addEventListener('canplay', this.handleVideoPlaying);
        video.addEventListener('waiting', this.handleVideoWaiting);
        video.addEventListener('error', this.handleVideoError, true);
      }
    });
    this.prevButton?.addEventListener('click', this.handlePrevClick);
    this.nextButton?.addEventListener('click', this.handleNextClick);
    this.track?.addEventListener('scroll', this.handleTrackScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);

    this.initMobileScrollPlay();
    this.updateControls();
  }

  disconnectedCallback() {
    this.cards.forEach((card) => {
      card.removeEventListener('mouseenter', this.handleCardMouseEnter);
      card.removeEventListener('mouseleave', this.handleCardMouseLeave);
      const muteBtn = card.querySelector('[data-influencer-toggle-mute]');
      if (muteBtn) {
        muteBtn.removeEventListener('click', this.handleMuteClick);
      }
      const video = card.querySelector('video');
      if (video) {
        video.removeEventListener('playing', this.handleVideoPlaying);
        video.removeEventListener('canplay', this.handleVideoPlaying);
        video.removeEventListener('waiting', this.handleVideoWaiting);
        video.removeEventListener('error', this.handleVideoError, true);
      }
    });
    this.prevButton?.removeEventListener('click', this.handlePrevClick);
    this.nextButton?.removeEventListener('click', this.handleNextClick);
    this.track?.removeEventListener('scroll', this.handleTrackScroll);
    window.removeEventListener('resize', this.handleResize);
    this.observer?.disconnect();
    this.scrollObserver?.disconnect();
    this.pauseAll();
  }

  /* ── Desktop hover ── */
  handleCardMouseEnter(event) {
    if (!this.desktopQuery.matches) return;

    const card = event.currentTarget;
    card.classList.add('is-hovered');
    this.playVideo(card);
  }

  handleCardMouseLeave(event) {
    if (!this.desktopQuery.matches) return;

    const card = event.currentTarget;
    card.classList.remove('is-hovered');

    const video = card.querySelector('video');
    if (video) {
      video.pause();
      card.classList.remove('is-playing', 'is-loading');
      // Re-mute on pause
      video.muted = true;
      card.classList.add('is-muted');
    }
  }

  /* ── Navigation ── */
  handlePrevClick() {
    this.scrollByCard(-1);
  }

  handleNextClick() {
    this.scrollByCard(1);
  }

  handleTrackScroll() {
    this.updateControls();
  }

  handleResize() {
    this.updateControls();
  }

  /* ── Mute toggle ── */
  handleMuteClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const card = button.closest('[data-influencer-card]');
    const video = card?.querySelector('video');
    if (!card || !video) return;

    video.muted = !video.muted;
    card.classList.toggle('is-muted', video.muted);
  }

  scrollByCard(direction) {
    const firstItem = this.track?.querySelector('.homepage-influencer-videos__item');
    if (!this.track || !firstItem) return;

    const trackStyles = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap || '0');
    const distance = firstItem.getBoundingClientRect().width + gap;

    this.track.scrollBy({ left: direction * distance, behavior: 'smooth' });
  }

  updateControls() {
    if (!this.track || !this.prevButton || !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const currentScroll = this.track.scrollLeft;
    const hasOverflow = maxScroll > 1;

    this.prevButton.disabled = !hasOverflow || currentScroll <= 1;
    this.nextButton.disabled = !hasOverflow || currentScroll >= maxScroll - 1;
  }

  /* ── Mobile scroll-to-play ── */
  initMobileScrollPlay() {
    if (!('IntersectionObserver' in window) || this.reducedMotionQuery.matches) return;

    // root:null → 以页面视口为准，天然实现「section 垂直进视口 + 卡片横向居中」的双重门控：
    // section 还没滚进视口时卡片可见比例为 0，不加载；进入视口后再对居中卡片触发懒加载 + 播放。
    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (this.desktopQuery.matches) return;

        entries.forEach((entry) => {
          const card = entry.target;
          const video = card.querySelector('video');
          if (!video) return;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            // 受「默认播放第一个可用视频」开关控制：关闭时移动端不自动播放
            if (this.autoplayFirst) {
              this.playVideo(card);
            }
          } else {
            video.pause();
            card.classList.remove('is-playing', 'is-loading');
            // Re-mute when scrolling away
            video.muted = true;
            card.classList.add('is-muted');
          }
        });
      },
      {
        root: null,
        threshold: 0.5,
      }
    );

    this.cards.forEach((card) => {
      this.scrollObserver.observe(card);
    });
  }

  /* ── Video playback ── */
  playVideo(card) {
    if (this.reducedMotionQuery.matches) return;

    const video = card.querySelector('video');
    if (!video) return;

    // Pause all other videos and re-mute them
    this.videos.forEach((item) => {
      if (item !== video) {
        item.pause();
        const otherCard = item.closest('[data-influencer-card]');
        if (otherCard) {
          otherCard.classList.remove('is-playing', 'is-loading');
          item.muted = true;
          otherCard.classList.add('is-muted');
        }
      }
    });

    // 懒加载：首次播放前才注入 src 并 load（hydrateVideo 内有守卫，只做一次）
    this.hydrateVideo(video);

    // 缓冲不足时显示转圈；playing / canplay 事件会移除 is-loading
    if (video.readyState < 3) {
      card.classList.add('is-loading');
    }

    // Play muted first (browser autoplay policy), then try to unmute.
    // If the browser blocks unmuted playback, fall back to muted.
    video.muted = true;
    video.playsInline = true;

    card.classList.add('is-playing');

    const playPromise = video.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(() => {
        if (!this.autoplaySound) {
          video.muted = true;
          card.classList.add('is-muted');
          return;
        }

        // Watch for browser force-pause when we unmute
        let forcedPause = false;
        const onPause = () => { forcedPause = true; };
        video.addEventListener('pause', onPause, { once: true });

        video.muted = false;
        card.classList.remove('is-muted');

        // Give the browser a moment to react to the unmute
        setTimeout(() => {
          video.removeEventListener('pause', onPause);
          if (forcedPause) {
            // Browser blocked unmuted playback — re-mute and resume
            video.muted = true;
            card.classList.add('is-muted');
            video.play().catch(() => {});
          }
        }, 150);
      }).catch(() => {});
    }
  }

  pauseAll() {
    this.videos.forEach((video) => {
      video.pause();
      const card = video.closest('[data-influencer-card]');
      if (card) {
        card.classList.remove('is-playing', 'is-loading');
      }
    });
  }

  /* ── Lazy-load: 注入 data-src → load，带守卫只做一次，加载过的视频不再重新下载 ── */
  hydrateVideo(video) {
    if (!video || video.dataset.influencerSourcesReady === 'true') return false;

    const sources = video.querySelectorAll('source[data-src]');
    if (!sources.length) return false;

    sources.forEach((source) => {
      if (!source.getAttribute('src')) {
        source.setAttribute('src', source.dataset.src);
      }
    });

    video.dataset.influencerSourcesReady = 'true';
    video.load();
    return true;
  }

  /* ── Loading state (spinner) ── */
  handleVideoPlaying(event) {
    const card = event.target.closest('[data-influencer-card]');
    card?.classList.remove('is-loading');
  }

  handleVideoWaiting(event) {
    const card = event.target.closest('[data-influencer-card]');
    if (card && card.classList.contains('is-playing')) {
      card.classList.add('is-loading');
    }
  }

  handleVideoError(event) {
    const card = event.target.closest('[data-influencer-card]');
    if (card) {
      card.classList.remove('is-loading', 'is-playing');
    }
  }
}

if (!customElements.get('homepage-influencer-videos')) {
  customElements.define('homepage-influencer-videos', HomepageInfluencerVideos);
}